<script setup lang="ts">
import { ref, computed, watch, h } from 'vue';
import { NTag, type DataTableColumns } from 'naive-ui';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import type { ContractManagementContractInDb } from '@/api';
import { ContractsService } from '@/services/contractManagement/contractsService';
import { logger } from '@/utils/logger';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

interface Props {
  /** When true, fetches contracts (e.g. when parent modal is open). */
  active: boolean;
  /** Selected contract (for v-model:selected). */
  selected?: ContractManagementContractInDb | null;
  /** Matter UIDs to exclude (contracts linked to these matters are excluded). */
  excludeMatterUids?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  selected: null,
  excludeMatterUids: () => [],
});

const emit = defineEmits<{
  'update:selected': [value: ContractManagementContractInDb | null];
}>();

const message = useGaiaToast();

const contractSearchQuery = ref('');
const contracts = ref<ContractManagementContractInDb[]>([]);
const contractLoading = ref(false);
const contractPage = ref(1);
const contractPageSize = ref(10);
const contractTotalCount = ref(0);
const hasAttemptedFetch = ref(false);

// The search field owns the debounce and fires this at once when it is cleared.
async function applySearch(): Promise<void> {
  contractPage.value = 1;
  await fetchContracts();
}

async function fetchContracts(): Promise<void> {
  contractLoading.value = true;
  hasAttemptedFetch.value = true;
  try {
    const result = await ContractsService.searchContracts({
      text: contractSearchQuery.value,
      page: contractPage.value,
      pageSize: contractPageSize.value,
      sortKey: 'created_at',
      sortDirection: 'desc',
      excludeMatterUids: props.excludeMatterUids.length > 0 ? props.excludeMatterUids : undefined,
    });
    contracts.value = result.results;
    contractTotalCount.value = result.result_count;
  } catch (error) {
    logger.error('Error fetching contracts:', error);
    message.error('Failed to load contracts');
    contracts.value = [];
    contractTotalCount.value = 0;
  } finally {
    contractLoading.value = false;
  }
}

function handleContractSelection(keys: (string | number)[]): void {
  if (keys.length === 0) {
    emit('update:selected', null);
    return;
  }
  const selectedUid = String(keys[0]);
  const contract = contracts.value.find(c => c.uid === selectedUid) ?? null;
  emit('update:selected', contract);
}

const selectedContractKeys = computed((): string[] => (props.selected ? [props.selected.uid] : []));

const contractColumns = computed((): DataTableColumns<ContractManagementContractInDb> => [
  { type: 'selection' },
  { title: 'Name', key: 'name', ellipsis: { tooltip: true } },
  {
    title: 'Type',
    key: 'type',
    width: 150,
    render(row): ReturnType<typeof h> {
      return h(
        NTag,
        { type: 'info', size: 'small' },
        { default: () => row.contract_type?.name ?? '—' }
      );
    },
  },
  {
    title: 'Description',
    key: 'description',
    ellipsis: { tooltip: true },
    render(row): ReturnType<typeof h> {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      return h('span', row.description || '—');
    },
  },
  {
    title: 'Tags',
    key: 'tags',
    width: 200,
    render(row): ReturnType<typeof h> {
      if (row.tags.length === 0) return h('span', '—');
      return h(
        'div',
        { style: { display: 'flex', flexWrap: 'wrap', gap: '4px' } },
        row.tags.map(tag => h(NTag, { size: 'small' }, { default: () => tag }))
      );
    },
  },
]);

const contractPagination = computed(() => ({
  page: contractPage.value,
  pageSize: contractPageSize.value,
  itemCount: contractTotalCount.value,
  showSizePicker: true,
  pageSizes: [10, 20, 50],
  onUpdatePage: (page: number): void => {
    contractPage.value = page;
  },
  onUpdatePageSize: (size: number): void => {
    contractPageSize.value = size;
    contractPage.value = 1;
  },
}));

watch(
  () => props.active,
  (active): void => {
    if (active) {
      contractSearchQuery.value = '';
      contractPage.value = 1;
      emit('update:selected', null);
      contracts.value = [];
      contractTotalCount.value = 0;
      hasAttemptedFetch.value = false;
      void fetchContracts();
    }
  },
  { immediate: true }
);

watch(contractPage, (): void => {
  if (contractPage.value > 0 && hasAttemptedFetch.value) void fetchContracts();
});
watch(contractPageSize, (): void => {
  if (contractPageSize.value > 0 && hasAttemptedFetch.value) {
    contractPage.value = 1;
    void fetchContracts();
  }
});
</script>

<template>
  <div class="contract-select-table">
    <div style="margin-bottom: 16px">
      <gaia-search-input
        v-model:value="contractSearchQuery"
        variant="panel"
        size="medium"
        placeholder="Search contracts"
        :debounce="500"
        @search="applySearch"
      />
    </div>
    <gaia-table
      :columns="contractColumns"
      :data="contracts"
      :loading="contractLoading"
      :pagination="contractPagination"
      :row-key="(row: ContractManagementContractInDb) => row.uid"
      :checked-row-keys="selectedContractKeys"
      :striped="true"
      :remote="true"
      @update:checked-row-keys="handleContractSelection"
    />
    <NEmpty
      v-if="!contractLoading && contracts.length === 0 && hasAttemptedFetch"
      description="No contracts found"
      style="padding: 40px 0"
    />
  </div>
</template>

<style scoped>
.contract-select-table {
  min-height: 320px;
}
</style>
